import { customAlphabet } from 'nanoid'
import { defineStore } from 'pinia'
import { CreatingElement, TextFormatPainter } from '@/types/edit'
import { ToolbarStates } from '@/types/toolbar'
import { DialogForExportTypes } from '@/types/export'
import { SYS_FONTS } from '@/configs/font'
import { TextAttrs, defaultRichTextAttrs } from '@/utils/prosemirror/utils'
import { isSupportFont } from '@/utils/font'

import { useSlidesStore } from './slides'

export interface MainState {
  activeElementIdList: string[]
  handleElementId: string
  activeGroupElementId: string
  hiddenElementIdList: string[]
  canvasPercentage: number
  canvasScale: number
  canvasDragged: boolean
  thumbnailsFocus: boolean
  editorAreaFocus: boolean
  disableHotkeys: boolean
  gridLineSize: number
  showRuler: boolean
  creatingElement: CreatingElement | null
  availableFonts: typeof SYS_FONTS
  toolbarState: ToolbarStates
  clipingImageElementId: string
  isScaling: boolean
  richTextAttrs: TextAttrs
  selectedTableCells: string[]
  selectedSlidesIndex: number[]
  dialogForExport: DialogForExportTypes
  databaseId: string
  textFormatPainter: TextFormatPainter | null
  showSelectPanel: boolean
  slideNanoid: string
  valueModelLoaded: boolean
  valueModelLoading: boolean
  slideEditorLoading: boolean,
  slideLibraryChooser: boolean,
  vpSlideId: string
  tableCellHookId: string
  tableFiltered: boolean
  activeDropzone: string
  editModeOn: boolean
  vmId: string
  slideLibraryActive: boolean
  editor: boolean
  calculatorId: string | null
  gateContent: boolean
  mergeExistingSlide: boolean,
  undoRedoEvent: boolean,
  recalculate: any,
  saving: boolean,
  lastModifiedInfo: any,
  themeDialogVisible: boolean,
  updated: any,
  advancedLayout: boolean
}

const nanoid = customAlphabet('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz')
export const databaseId = nanoid(10)

export const useMainStore = defineStore('main', {
  state: (): MainState => ({
    activeElementIdList: [], // A collection of selected element IDs, including handleElementId
    handleElementId: '', // The element ID being operated on
    activeGroupElementId: '', // Among the combined element members, the ID of the selected element that can be operated independently
    hiddenElementIdList: [], // A collection of hidden element IDs
    canvasPercentage: 90, // Canvas viewable area percentage
    canvasScale: 1, // Canvas scaling (based on width 1000px)
    canvasDragged: false, // The canvas is dragged and moved
    thumbnailsFocus: false, // Left navigation thumbnail area focused
    editorAreaFocus: false, //  edit area focus
    disableHotkeys: false, // Disable shortcut keys
    gridLineSize: 0, // Gridline size (0 means no gridlines)
    showRuler: false, // show ruler
    creatingElement: null, // The element information being inserted needs to be inserted by drawing the element (text, shape, line)
    availableFonts: SYS_FONTS, // Fonts available in the current environment
    toolbarState: ToolbarStates.SLIDE_DESIGN, // Right Toolbar Status
    clipingImageElementId: '', // The ID of the picture currently being cropped
    richTextAttrs: defaultRichTextAttrs, // rich text state
    selectedTableCells: [], // selected table cell
    isScaling: false, // element scaling in progress
    selectedSlidesIndex: [], // The currently selected page index collection
    dialogForExport: '', // export panel
    databaseId, // Identifies the indexedDB database ID of the current application
    textFormatPainter: null, // text formatting brush
    showSelectPanel: false, // Open the selection panel
    slideNanoid: '',
    valueModelLoading: false,
    valueModelLoaded: false,
    slideEditorLoading: true,
    vpSlideId: '',
    tableCellHookId: '',
    tableFiltered: true,
    activeDropzone: '',
    editModeOn: true,
    vmId: '',
    slideLibraryActive: false,
    slideLibraryChooser: false,
    editor: true,
    calculatorId: null,
    gateContent: true,
    mergeExistingSlide: false,
    undoRedoEvent: false,
    recalculate: null,
    saving: false,
    lastModifiedInfo: null,
    themeDialogVisible: false,
    updated: Date.now(),
    advancedLayout: false
  }),

  getters: {
    activeElementList(state) {
      const slidesStore = useSlidesStore()
      const currentSlide = slidesStore.currentSlide
      if (!currentSlide || !currentSlide.elements) return []
      return currentSlide.elements.filter(element => state.activeElementIdList.includes(element.id))
    },

    handleElement(state) {
      const slidesStore = useSlidesStore()
      const currentSlide = slidesStore.currentSlide
      if (!currentSlide || !currentSlide.elements) return null
      return currentSlide.elements.find(element => state.handleElementId === element.id) || null
    },
  },

  actions: {
    setActiveElementIdList(activeElementIdList: string[]) {
      if (activeElementIdList.length === 1) this.handleElementId = activeElementIdList[0]
      else this.handleElementId = ''

      this.activeElementIdList = activeElementIdList
    },

    setHandleElementId(handleElementId: string) {
      this.handleElementId = handleElementId
    },

    setActiveGroupElementId(activeGroupElementId: string) {
      this.activeGroupElementId = activeGroupElementId
    },

    setHiddenElementIdList(hiddenElementIdList: string[]) {
      this.hiddenElementIdList = hiddenElementIdList
    },

    setCanvasPercentage(percentage: number) {
      this.canvasPercentage = percentage
    },

    setCanvasScale(scale: number) {
      this.canvasScale = scale
    },

    setCanvasDragged(isDragged: boolean) {
      this.canvasDragged = isDragged
    },

    setThumbnailsFocus(isFocus: boolean) {
      this.thumbnailsFocus = isFocus
    },

    setEditorareaFocus(isFocus: boolean) {
      this.editorAreaFocus = isFocus
    },

    setDisableHotkeysState(disable: boolean) {
      this.disableHotkeys = disable
    },

    setGridLineSize(size: number) {
      this.gridLineSize = size
    },

    setRulerState(show: boolean) {
      this.showRuler = show
    },

    setCreatingElement(element: CreatingElement | null) {
      this.creatingElement = element
    },

    setAvailableFonts() {
      this.availableFonts = SYS_FONTS.filter(font => isSupportFont(font.value))
    },

    setToolbarState(toolbarState: ToolbarStates) {
      this.toolbarState = toolbarState
    },

    setClipingImageElementId(elId: string) {
      this.clipingImageElementId = elId
    },

    setRichtextAttrs(attrs: TextAttrs) {
      this.richTextAttrs = attrs
    },

    setSelectedTableCells(cells: string[]) {
      this.selectedTableCells = cells
    },

    setScalingState(isScaling: boolean) {
      this.isScaling = isScaling
    },

    updateSelectedSlidesIndex(selectedSlidesIndex: number[]) {
      this.selectedSlidesIndex = selectedSlidesIndex
    },

    setDialogForExport(type: DialogForExportTypes) {
      this.dialogForExport = type
    },

    setTextFormatPainter(textFormatPainter: TextFormatPainter | null) {
      this.textFormatPainter = textFormatPainter
    },

    setSelectPanelState(show: boolean) {
      this.showSelectPanel = show
    },

    handleToggleButton() {
      const elementContents = document.querySelectorAll('.element-content.dragdrop')
      const listGroupElements = document.querySelectorAll('.list-group')
      const listGroupItem = document.querySelectorAll('.list-group-item')

      elementContents.forEach(element => {
        element.style.border = !this.editModeOn ? 'none' : 'none'
        element.style.cursor = !this.editModeOn ? 'default' : 'move'
      })

      listGroupElements.forEach(element => {
        element.style.cursor = !this.editModeOn ? 'default' : 'move'
      })

      listGroupItem.forEach(element => {
        element.style.cursor = !this.editModeOn ? 'default' : 'move'

        const proseMirrorChild = element.querySelector('[contenteditable]')
        if (!this.editModeOn) element.classList.remove('focusStyle')

        if (proseMirrorChild) {
          proseMirrorChild.contentEditable = this.editModeOn

          !this.editModeOn ? proseMirrorChild.style.cursor = 'default' : proseMirrorChild.style.cursor = 'text'
        }
      })
    }
  },
})
